import { initials } from '../lib/formatters';
import styles from './LiveTopBar.module.css';

export type LiveTopBarProps = {
  teamShort: string;
  wins: number;
  losses: number;
  roundNumber: number | null;
  mapLabel: string;
  side: string | null;
  userName: string;
  roleName: string | null;
  onExit: () => void;
  /** Optional secondary action (e.g. Controls only for strategists) */
  secondaryAction?: { label: string; onClick: () => void } | null;
};

export default function LiveTopBar({
  teamShort,
  wins,
  losses,
  roundNumber,
  mapLabel,
  side,
  userName,
  roleName,
  onExit,
  secondaryAction,
}: LiveTopBarProps) {
  const sideNorm = (side ?? '').toUpperCase();
  const sideClass = sideNorm === 'CT' ? styles.sideCt : sideNorm === 'T' ? styles.sideT : '';

  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        <div className={styles.mark} aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3h4l1 3-1 2h-4l-1-2z" />
            <path d="M10 8l-2 8 4 7 4-7-2-8" />
          </svg>
        </div>
        <div className={styles.brandText}>
          <div className={styles.name}>Stratos</div>
          <div className={styles.liveTag}>
            <span className={styles.dot} /> Live Session
          </div>
        </div>
      </div>

      <div className={styles.matchCtx} aria-label="Match score">
        <div className={styles.teamScore}>
          <span className={styles.team}>{teamShort}</span>
          <span className={styles.score}>{wins}</span>
        </div>
        <span className={styles.sep}>–</span>
        <span className={styles.opp}>{losses}</span>
        {(roundNumber != null || mapLabel || side) && (
          <>
            <span className={styles.divider} aria-hidden="true" />
            <div className={styles.meta}>
              {roundNumber != null && <span className={styles.round}>R{roundNumber}</span>}
              {roundNumber != null && mapLabel && <span className={styles.dotSep} />}
              {mapLabel && <span>{mapLabel}</span>}
              {side && (
                <>
                  <span className={styles.dotSep} />
                  <span className={`${styles.side} ${sideClass}`}>
                    {sideNorm === 'CT' ? 'CT-Side' : sideNorm === 'T' ? 'T-Side' : side}
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.actions}>
        {secondaryAction && (
          <button type="button" className={styles.secondary} onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </button>
        )}
        <div className={styles.me} title="You">
          <span className={styles.av} aria-hidden="true">
            {initials(userName)}
          </span>
          <div>
            <div className={styles.handle}>{userName}</div>
            {roleName && <div className={styles.role}>{roleName}</div>}
          </div>
        </div>
        <button type="button" className={styles.exit} onClick={onExit}>
          Exit
        </button>
      </div>
    </header>
  );
}
